import React, { useState, useEffect } from 'react';
import {
  Table, Tag, Button, Popconfirm, Space, Input, Modal, Form, Select,
  Typography, Switch, Card, message, InputNumber, Tooltip, Badge,
  Descriptions, Alert, Row, Col, Statistic
} from 'antd';
import {
  PlusOutlined, EditOutlined, DeleteOutlined, AppstoreOutlined,
  ArrowUpOutlined, ArrowDownOutlined, ReloadOutlined,
  SettingOutlined, FolderOutlined, InfoCircleOutlined
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import api from '../api';

const { Title, Text } = Typography;

const statusMap = {
  normal: { label: '正常', color: 'green' },
  disabled: { label: '已禁用', color: 'default' },
};

function MiniProgramCategoryPage() {
  const navigate = useNavigate();
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [createModal, setCreateModal] = useState(false);
  const [editModal, setEditModal] = useState(false);
  const [selected, setSelected] = useState(null);
  const [form] = Form.useForm();
  const [editForm] = Form.useForm();

  const loadCategories = async () => {
    setLoading(true);
    try {
      const { list } = await api.get('/admin/mini-programs/categories', { params: { limit: 100 } });
      setCategories(list || []);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadCategories();
  }, []);

  const handleCreate = async () => {
    try {
      const values = await form.validateFields();
      await api.post('/admin/mini-programs/categories', values);
      message.success('分类创建成功');
      setCreateModal(false);
      form.resetFields();
      loadCategories();
    } catch (e) {
      message.error(e.errorFields ? '请填写完整' : '创建失败');
    }
  };

  const handleEdit = (record) => {
    setSelected(record);
    editForm.setFieldsValue(record);
    setEditModal(true);
  };

  const handleEditSave = async () => {
    try {
      const values = await editForm.validateFields();
      await api.put(`/admin/mini-programs/categories/${selected.id}`, values);
      message.success('分类更新成功');
      setEditModal(false);
      loadCategories();
    } catch (e) {
      message.error('更新失败');
    }
  };

  const handleDelete = async (id) => {
    try {
      await api.delete(`/admin/mini-programs/categories/${id}`);
      message.success('分类已删除');
      loadCategories();
    } catch (e) {
      message.error('删除失败，请先移除该分类下的小程序');
    }
  };

  const handleStatusToggle = async (id, currentStatus) => {
    const newStatus = currentStatus === 'normal' ? 'disabled' : 'normal';
    try {
      await api.put(`/admin/mini-programs/categories/${id}`, { status: newStatus });
      message.success(newStatus === 'normal' ? '已启用' : '已禁用');
      loadCategories();
    } catch (e) {
      message.error('操作失败');
    }
  };

  const handleMove = async (index, direction) => {
    const newCategories = [...categories];
    const target = index + direction;
    if (target < 0 || target >= newCategories.length) return;
    [newCategories[index], newCategories[target]] = [newCategories[target], newCategories[index]];
    newCategories.forEach((c, i) => (c.sort_order = i + 1));
    setCategories(newCategories);
    // 批量更新排序
    try {
      await api.put('/admin/mini-programs/categories/sort', {
        orders: newCategories.map((c, i) => ({ id: c.id, sort_order: i + 1 })),
      });
      message.success('排序已更新');
    } catch (e) {
      message.error('排序更新失败');
      loadCategories();
    }
  };

  const columns = [
    { title: 'ID', dataIndex: 'id', width: 60 },
    {
      title: '分类名称', width: 180,
      render: (_, r) => (
        <Space>
          <FolderOutlined style={{ color: '#faad14' }} />
          <Text strong>{r.name}</Text>
        </Space>
      ),
    },
    {
      title: '分类标识', width: 160,
      render: (_, r) => <Tag color="blue" style={{ fontFamily: 'monospace' }}>{r.identifier || '-'}</Tag>,
    },
    { title: '描述', dataIndex: 'description', ellipsis: true },
    {
      title: '小程序数量', width: 100,
      align: 'center',
      render: (_, r) => (
        <Badge count={r.program_count || 0} style={{ backgroundColor: '#52c41a' }} />
      ),
    },
    {
      title: '排序', width: 80,
      align: 'center',
      render: (_, r) => <Text>{r.sort_order || 0}</Text>,
    },
    {
      title: '状态', width: 100,
      render: (_, r) => (
        <Tag color={statusMap[r.status]?.color}>{statusMap[r.status]?.label || r.status}</Tag>
      ),
    },
    { title: '创建时间', dataIndex: 'created_at', width: 160 },
    {
      title: '操作', width: 240,
      render: (_, r, index) => (
        <Space size="small">
          <Tooltip title="上移">
            <Button
              size="small"
              icon={<ArrowUpOutlined />}
              disabled={index === 0}
              onClick={() => handleMove(index, -1)}
            />
          </Tooltip>
          <Tooltip title="下移">
            <Button
              size="small"
              icon={<ArrowDownOutlined />}
              disabled={index === categories.length - 1}
              onClick={() => handleMove(index, 1)}
            />
          </Tooltip>
          <Switch
            size="small"
            checked={r.status === 'normal'}
            onChange={() => handleStatusToggle(r.id, r.status)}
            checkedChildren="启用"
            unCheckedChildren="禁用"
          />
          <Button size="small" icon={<EditOutlined />} onClick={() => handleEdit(r)}>编辑</Button>
          <Popconfirm
            title="确定删除该分类?"
            description="删除前请先移除该分类下的小程序"
            onConfirm={() => handleDelete(r.id)}
          >
            <Button size="small" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const stats = {
    total: categories.length,
    normal: categories.filter(c => c.status === 'normal').length,
    disabled: categories.filter(c => c.status === 'disabled').length,
    totalPrograms: categories.reduce((sum, c) => sum + (c.program_count || 0), 0),
  };

  return (
    <>
      {/* 顶部导航 */}
      <div style={{ marginBottom: 16 }}>
        <Space>
          <Button icon={<AppstoreOutlined />} onClick={() => navigate('/mini-programs')}>
            返回小程序列表
          </Button>
        </Space>
      </div>

      {/* 顶部操作栏 */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <Title level={4} style={{ margin: 0 }}>
          <SettingOutlined /> 小程序分类管理
        </Title>
        <Space>
          <Button icon={<ReloadOutlined />} onClick={loadCategories}>刷新</Button>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => setCreateModal(true)}
            style={{ background: '#07C160', borderColor: '#07C160' }}
          >
            新增分类
          </Button>
        </Space>
      </div>

      {/* 统计卡片 */}
      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col span={6}>
          <Card size="small">
            <Statistic
              title="分类总数"
              value={stats.total}
              prefix={<FolderOutlined style={{ color: '#faad14' }} />}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card size="small">
            <Statistic
              title="启用中"
              value={stats.normal}
              prefix={<FolderOutlined style={{ color: '#52c41a' }} />}
              valueStyle={{ color: '#52c41a' }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card size="small">
            <Statistic
              title="已禁用"
              value={stats.disabled}
              prefix={<FolderOutlined style={{ color: '#bfbfbf' }} />}
              valueStyle={{ color: '#bfbfbf' }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card size="small">
            <Statistic
              title="小程序总数"
              value={stats.totalPrograms}
              prefix={<AppstoreOutlined style={{ color: '#07C160' }} />}
              valueStyle={{ color: '#07C160' }}
            />
          </Card>
        </Col>
      </Row>

      {/* 分类列表 */}
      <Card size="small">
        <Table
          columns={columns}
          dataSource={categories}
          rowKey="id"
          loading={loading}
          size="small"
          pagination={{ pageSize: 20, showTotal: (total) => `共 ${total} 条` }}
        />
      </Card>

      {/* 提示信息 */}
      <Card size="small" style={{ marginTop: 16, background: '#e6f7ff', border: '1px solid #91d5ff' }}>
        <Space>
          <InfoCircleOutlined style={{ color: '#1890ff' }} />
          <Text type="secondary">
            提示：分类用于小程序的分类展示，支持排序和启用/禁用。禁用后该分类下的小程序将不在前台展示。
          </Text>
        </Space>
      </Card>

      {/* 创建分类弹窗 */}
      <Modal
        title={<span><PlusOutlined /> 新增分类</span>}
        open={createModal}
        onOk={handleCreate}
        onCancel={() => { setCreateModal(false); form.resetFields(); }}
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="name"
            label="分类名称"
            rules={[{ required: true, message: '请输入分类名称' }]}
          >
            <Input placeholder="如: 工具类" />
          </Form.Item>
          <Form.Item
            name="identifier"
            label="分类标识"
            rules={[{ required: true, message: '请输入分类标识' }]}
            extra="英文标识，用于程序识别"
          >
            <Input placeholder="如: tools" />
          </Form.Item>
          <Form.Item
            name="description"
            label="描述"
          >
            <Input.TextArea rows={2} placeholder="分类描述信息" />
          </Form.Item>
          <Form.Item
            name="sort_order"
            label="排序值"
            initialValue={0}
            extra="数值越小排序越靠前"
          >
            <InputNumber style={{ width: '100%' }} min={0} />
          </Form.Item>
          <Form.Item
            name="status"
            label="状态"
            initialValue="normal"
          >
            <Select
              options={[
                { value: 'normal', label: '正常' },
                { value: 'disabled', label: '禁用' },
              ]}
            />
          </Form.Item>
        </Form>
      </Modal>

      {/* 编辑分类弹窗 */}
      <Modal
        title={<span><EditOutlined /> 编辑分类 - {selected?.name || ''}</span>}
        open={editModal}
        onOk={handleEditSave}
        onCancel={() => { setEditModal(false); setSelected(null); editForm.resetFields(); }}
      >
        <Form form={editForm} layout="vertical">
          <Form.Item
            name="name"
            label="分类名称"
            rules={[{ required: true, message: '请输入分类名称' }]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            name="identifier"
            label="分类标识"
            rules={[{ required: true, message: '请输入分类标识' }]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            name="description"
            label="描述"
          >
            <Input.TextArea rows={2} />
          </Form.Item>
          <Form.Item
            name="sort_order"
            label="排序值"
          >
            <InputNumber style={{ width: '100%' }} min={0} />
          </Form.Item>
          <Form.Item
            name="status"
            label="状态"
          >
            <Select
              options={[
                { value: 'normal', label: '正常' },
                { value: 'disabled', label: '禁用' },
              ]}
            />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
}

export default MiniProgramCategoryPage;
